import type { IconType } from 'react-icons';
import { GenIcon } from 'react-icons/lib';
import { LuCheck, LuCloudAlert, LuMinus, LuTrendingDown, LuTrendingUp, LuUserPlus } from 'react-icons/lu';
import {
  RxActivityLog,
  RxArchive,
  RxArrowDown,
  RxArrowLeft,
  RxArrowRight,
  RxArrowUp,
  RxAvatar,
  RxBell,
  RxBookmark,
  RxBookmarkFilled,
  RxCalendar,
  RxCaretSort,
  RxChatBubble,
  RxCheck,
  RxCheckCircled,
  RxChevronDown,
  RxChevronLeft,
  RxChevronRight,
  RxChevronUp,
  RxCircleBackslash,
  RxClipboard,
  RxClock,
  RxColumns,
  RxComponent1,
  RxComponent2,
  RxCopy,
  RxCornerBottomLeft,
  RxCross2,
  RxCrossCircled,
  RxDashboard,
  RxDividerHorizontal,
  RxDotsHorizontal,
  RxDotsVertical,
  RxDoubleArrowDown,
  RxDoubleArrowLeft,
  RxDoubleArrowRight,
  RxDoubleArrowUp,
  RxDownload,
  RxDragHandleDots2,
  RxDrawingPin,
  RxEnvelopeClosed,
  RxExclamationCircled,
  RxExclamationMark,
  RxExternalLink,
  RxEyeNone,
  RxEyeOpen,
  RxFile,
  RxGear,
  RxGlobe,
  RxHamburgerMenu,
  RxInfoCircled,
  RxLayers,
  RxLayout,
  RxLink2,
  RxMagnifyingGlass,
  RxMinus,
  RxMove,
  RxPencil1,
  RxPerson,
  RxPlus,
  RxQuestionMarkCircled,
  RxReset,
  RxRulerHorizontal,
  RxRulerSquare,
  RxShuffle,
  RxSize,
  RxSlider,
  RxStar,
  RxTable,
  RxTrash,
  RxUpdate,
  RxUpload,
  RxViewVertical,
} from 'react-icons/rx';

/** A round speech bubble with its point at the bottom left, the shape of a comment pin. */
const CommentIcon = GenIcon({
  tag: 'svg',
  attr: { viewBox: '0 0 15 15', fill: 'none' },
  child: [
    { tag: 'path', attr: { d: 'M2.5 12.5V7.5a5 5 0 1 1 5 5Z', stroke: 'currentColor', strokeWidth: '1' }, child: [] },
  ],
});

export type IconName =
  | 'activityLog'
  | 'archive'
  | 'arrowDown'
  | 'arrowLeft'
  | 'arrowRight'
  | 'arrowUp'
  | 'avatar'
  | 'bell'
  | 'bookmark'
  | 'bookmarkFilled'
  | 'calendar'
  | 'caretSort'
  | 'chatBubble'
  | 'check'
  | 'checkCircled'
  | 'chevronDown'
  | 'chevronLeft'
  | 'chevronRight'
  | 'chevronUp'
  | 'chevronsUpDown'
  | 'circleBackslash'
  | 'clipboard'
  | 'clock'
  | 'column'
  | 'comment'
  | 'component1'
  | 'component2'
  | 'copy'
  | 'cornerBottomLeft'
  | 'cross2'
  | 'crossCircled'
  | 'dashboard'
  | 'dividerHorizontal'
  | 'drawingPin'
  | 'dotsHorizontal'
  | 'dotsVertical'
  | 'doubleArrowDown'
  | 'doubleArrowLeft'
  | 'doubleArrowRight'
  | 'doubleArrowUp'
  | 'download'
  | 'dragHandleDots2'
  | 'edit'
  | 'envelopeClosed'
  | 'externalLink'
  | 'eyeNone'
  | 'eyeOpen'
  | 'basemap'
  | 'file'
  | 'gear'
  | 'infoCircled'
  | 'layers'
  | 'layoutSplit'
  | 'legend'
  | 'link2'
  | 'luCheck'
  | 'luCloudAlert'
  | 'luMinus'
  | 'luTrendingDown'
  | 'luTrendingUp'
  | 'luUserPlus'
  | 'magnifyingGlass'
  | 'exclamationMark'
  | 'shuffle'
  | 'minus'
  | 'move'
  | 'person'
  | 'plus'
  | 'questionMarkCircled'
  | 'reset'
  | 'ruler'
  | 'rulerHorizontal'
  | 'size'
  | 'slider'
  | 'exclamationCircled'
  | 'star'
  | 'table'
  | 'trash'
  | 'update'
  | 'upload'
  | 'viewVertical';

export const IconMap: { [Icon in IconName]: IconType } = {
  activityLog: RxActivityLog,
  archive: RxArchive,
  arrowDown: RxArrowDown,
  arrowLeft: RxArrowLeft,
  arrowRight: RxArrowRight,
  arrowUp: RxArrowUp,
  avatar: RxAvatar,
  bell: RxBell,
  bookmark: RxBookmark,
  bookmarkFilled: RxBookmarkFilled,
  calendar: RxCalendar,
  caretSort: RxCaretSort,
  chatBubble: RxChatBubble,
  check: RxCheck,
  checkCircled: RxCheckCircled,
  chevronDown: RxChevronDown,
  chevronLeft: RxChevronLeft,
  chevronRight: RxChevronRight,
  chevronUp: RxChevronUp,
  chevronsUpDown: RxDoubleArrowDown,
  circleBackslash: RxCircleBackslash,
  clipboard: RxClipboard,
  clock: RxClock,
  column: RxColumns,
  comment: CommentIcon,
  component1: RxComponent1,
  component2: RxComponent2,
  copy: RxCopy,
  cornerBottomLeft: RxCornerBottomLeft,
  cross2: RxCross2,
  crossCircled: RxCrossCircled,
  dashboard: RxDashboard,
  dividerHorizontal: RxDividerHorizontal,
  drawingPin: RxDrawingPin,
  dotsHorizontal: RxDotsHorizontal,
  dotsVertical: RxDotsVertical,
  doubleArrowDown: RxDoubleArrowDown,
  doubleArrowLeft: RxDoubleArrowLeft,
  doubleArrowRight: RxDoubleArrowRight,
  doubleArrowUp: RxDoubleArrowUp,
  download: RxDownload,
  dragHandleDots2: RxDragHandleDots2,
  edit: RxPencil1,
  exclamationMark: RxExclamationMark,
  exclamationCircled: RxExclamationCircled,
  envelopeClosed: RxEnvelopeClosed,
  externalLink: RxExternalLink,
  eyeNone: RxEyeNone,
  eyeOpen: RxEyeOpen,
  basemap: RxGlobe,
  file: RxFile,
  gear: RxGear,
  infoCircled: RxInfoCircled,
  layers: RxLayers,
  layoutSplit: RxLayout,
  legend: RxHamburgerMenu,
  link2: RxLink2,
  luCheck: LuCheck,
  luCloudAlert: LuCloudAlert,
  luMinus: LuMinus,
  luTrendingDown: LuTrendingDown,
  luTrendingUp: LuTrendingUp,
  luUserPlus: LuUserPlus,
  magnifyingGlass: RxMagnifyingGlass,
  shuffle: RxShuffle,
  minus: RxMinus,
  move: RxMove,
  person: RxPerson,
  plus: RxPlus,
  questionMarkCircled: RxQuestionMarkCircled,
  reset: RxReset,
  rulerHorizontal: RxRulerHorizontal,
  ruler: RxRulerSquare,
  size: RxSize,
  slider: RxSlider,
  star: RxStar,
  table: RxTable,
  trash: RxTrash,
  update: RxUpdate,
  upload: RxUpload,
  viewVertical: RxViewVertical,
};

type Props = {
  icon: IconName;
  size?: number;
  className?: string;
  ariaLabel?: string;
};

export function Icon({ icon, size = 14, className, ariaLabel }: Props) {
  const IconComponent = IconMap[icon];
  return (
    <IconComponent
      size={size}
      className={className}
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
    />
  );
}
